import { z } from 'zod';

import { CashDenominationRowsSchema } from './cash-denominations.schema.js';
import { CashOperationIdentityDtoSchema } from './cash-operation-identity.schema.js';
import {
  cashCustodyReceiptSourceTypeValues,
  cashCustodyReceiptStatusValues,
  type CashCustodyReceiptDto,
  type CashCustodyReceiptSourceType,
  type CashCustodyReceiptStatus,
  type ListCashCustodyReceiptsRequest,
  type ListCashCustodyReceiptsResponse,
  type ReceiveMatchedTreasuryDeliveryRequest,
  type ReceiveProvisionalCashDeliveryRequest,
  type ReconcileCashCustodyReceiptRequest,
  type ReverseCashCustodyReceiptRequest,
} from './cash-custody-receipts.dto.js';

const timestampSchema = z.iso.datetime();

export const CashCustodyReceiptStatusSchema: z.ZodType<CashCustodyReceiptStatus> = z.enum(
  cashCustodyReceiptStatusValues,
);
export const CashCustodyReceiptSourceTypeSchema: z.ZodType<CashCustodyReceiptSourceType> = z.enum(
  cashCustodyReceiptSourceTypeValues,
);

export const CashCustodyReceiptDtoSchema: z.ZodType<CashCustodyReceiptDto> =
  CashOperationIdentityDtoSchema.extend({
    id: z.uuid(),
    status: CashCustodyReceiptStatusSchema,
    sourceType: CashCustodyReceiptSourceTypeSchema,
    sourceReference: z.string().trim().min(1).max(200),
    storeId: z.uuid(),
    locationId: z.uuid(),
    stationId: z.uuid(),
    targetVaultCashLocationId: z.uuid(),
    amountMinor: z.number().int().positive(),
    denominations: CashDenominationRowsSchema.nullable(),
    evidenceNote: z.string().trim().min(1).max(500).nullable(),
    treasuryInstructionId: z.uuid().nullable(),
    originalReceiptId: z.uuid().nullable(),
    vaultInMovementId: z.uuid().nullable(),
    vaultOutMovementId: z.uuid().nullable(),
    treasuryOutMovementId: z.uuid().nullable(),
    treasuryInMovementId: z.uuid().nullable(),
    reconciledByUserId: z.uuid().nullable(),
    reconciledAt: timestampSchema.nullable(),
    exceptionReason: z.string().trim().min(1).max(500).nullable(),
    reversedByReceiptId: z.uuid().nullable(),
    createdAt: timestampSchema,
    updatedAt: timestampSchema,
  }).superRefine((value, context) => {
    const reversal = value.sourceType === 'custody_receipt_reversal';
    const directAccepted =
      value.sourceType === 'direct_vault_intake' && value.status === 'accepted';
    const directCorrection =
      value.sourceType === 'direct_vault_intake' && value.status === 'corrected';
    if (
      reversal &&
      (value.status !== 'reversed' ||
        !value.originalReceiptId ||
        value.vaultInMovementId !== null ||
        !value.vaultOutMovementId ||
        value.treasuryInstructionId !== null ||
        value.treasuryOutMovementId !== null ||
        value.reversedByReceiptId !== null)
    ) {
      context.addIssue({
        code: 'custom',
        message: 'Custody receipt reversal evidence has an invalid linked movement shape.',
      });
    }
    if (
      !reversal &&
      (value.vaultInMovementId === null ||
        value.vaultOutMovementId !== null ||
        value.treasuryInMovementId !== null)
    ) {
      context.addIssue({
        code: 'custom',
        message: 'Original custody receipt evidence has an invalid linked movement shape.',
      });
    }
    if (directAccepted && value.originalReceiptId !== null) {
      context.addIssue({
        code: 'custom',
        message: 'An accepted direct Vault intake cannot replace another receipt.',
      });
    }
    if (directCorrection && value.originalReceiptId === null) {
      context.addIssue({
        code: 'custom',
        message: 'A corrected direct Vault intake must link the reversed receipt it replaces.',
      });
    }
    if (!reversal && value.originalReceiptId !== null && !directCorrection) {
      context.addIssue({
        code: 'custom',
        message: 'Only a corrected direct Vault intake can replace an original receipt.',
      });
    }
    if (
      (value.status === 'accepted' || value.status === 'corrected') &&
      value.sourceType !== 'direct_vault_intake'
    ) {
      context.addIssue({
        code: 'custom',
        message: 'Accepted and corrected receipt states are reserved for direct Vault intake.',
      });
    }
  });

export const ListCashCustodyReceiptsRequestSchema: z.ZodType<ListCashCustodyReceiptsRequest> =
  z.object({
    companyId: z.uuid(),
    locationId: z.uuid().optional(),
    targetVaultCashLocationId: z.uuid().optional(),
    status: CashCustodyReceiptStatusSchema.optional(),
  });

export const ListCashCustodyReceiptsResponseSchema: z.ZodType<ListCashCustodyReceiptsResponse> =
  z.object({ items: z.array(CashCustodyReceiptDtoSchema) });

const receiptBaseSchema = z.object({
  companyId: z.uuid(),
  operationId: z.uuid(),
  storeId: z.uuid(),
  locationId: z.uuid(),
  stationId: z.uuid(),
  targetVaultCashLocationId: z.uuid(),
  amountMinor: z.number().int().positive(),
  denominations: CashDenominationRowsSchema.nullable().optional(),
  occurredAt: timestampSchema.optional(),
});

export const ReceiveMatchedTreasuryDeliveryRequestSchema: z.ZodType<ReceiveMatchedTreasuryDeliveryRequest> =
  receiptBaseSchema.extend({ treasuryInstructionId: z.uuid() });

export const ReceiveProvisionalCashDeliveryRequestSchema: z.ZodType<ReceiveProvisionalCashDeliveryRequest> =
  receiptBaseSchema.extend({
    sourceReference: z.string().trim().min(1).max(200),
    evidenceNote: z.string().trim().min(1).max(500).nullable().optional(),
  });

export const ReconcileCashCustodyReceiptRequestSchema: z.ZodType<ReconcileCashCustodyReceiptRequest> =
  z
    .object({
      companyId: z.uuid(),
      operationId: z.uuid(),
      receiptId: z.uuid(),
      resolution: z.enum([
        'match_treasury_instruction',
        'reclassify_direct_vault_intake',
        'exception',
      ]),
      treasuryInstructionId: z.uuid().nullable().optional(),
      reason: z.string().trim().min(1).max(500).nullable().optional(),
      occurredAt: timestampSchema.optional(),
    })
    .superRefine((value, context) => {
      if (value.resolution === 'match_treasury_instruction' && !value.treasuryInstructionId) {
        context.addIssue({
          code: 'custom',
          path: ['treasuryInstructionId'],
          message: 'A Treasury instruction is required for a matched reconciliation.',
        });
      }
      if (value.resolution === 'exception' && !value.reason) {
        context.addIssue({
          code: 'custom',
          path: ['reason'],
          message: 'An exception reconciliation requires a reason.',
        });
      }
    });

export const ReverseCashCustodyReceiptRequestSchema: z.ZodType<ReverseCashCustodyReceiptRequest> =
  z.object({
    companyId: z.uuid(),
    operationId: z.uuid(),
    receiptId: z.uuid(),
    reason: z.string().trim().min(1).max(500),
    denominations: CashDenominationRowsSchema.nullable().optional(),
    occurredAt: timestampSchema.optional(),
  });
